import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { functions } from "../firebase";

type StudentRow = {
  id: string;
  studentKey: string;
  fullName: string;
  grade?: string;
  specialAssignments?: string[];
};

type SimplePerson = {
  id: string;
  uid?: string;
  email?: string;
  fullName?: string;
};

function asString(v: any) {
  return typeof v === "string" ? v.trim() : "";
}

function normalize(v: string) {
  return String(v || "").trim().toLowerCase();
}

function asStringArray(v: any) {
  if (!Array.isArray(v)) return [] as string[];
  return Array.from(new Set(v.map((x) => asString(x)).filter(Boolean)));
}

function labelStudent(s: StudentRow) {
  const extras = asStringArray(s.specialAssignments);
  const suffix = extras.length ? ` • ${extras.join(", ")}` : "";
  return s.grade ? `${s.fullName} (Grade ${s.grade})${suffix}` : `${s.fullName}${suffix}`;
}

function labelPerson(p: SimplePerson) {
  return p.fullName || p.email || p.uid || p.id;
}

function personKey(p: SimplePerson) {
  return asString(p.uid) || asString(p.id);
}

export function MessagingNewDirectPage() {
  const nav = useNavigate();

  const [students, setStudents] = useState<StudentRow[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(true);

  const [studentFilter, setStudentFilter] = useState("");
  const [studentId, setStudentId] = useState<string | null>(null);

  const [people, setPeople] = useState<SimplePerson[]>([]);
  const [peopleMode, setPeopleMode] = useState<"teachers" | "parents" | null>(null);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [peopleFilter, setPeopleFilter] = useState("");

  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  const [selectedParentIds, setSelectedParentIds] = useState<string[]>([]);
  const [includeOtherParents, setIncludeOtherParents] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoadingStudents(true);
      setErr(null);
      try {
        const fn = httpsCallable(functions, "getMyStudentsForTeacher");
        const res: any = await fn({});
        const out: StudentRow[] = Array.isArray(res?.data) ? res.data : [];
        out.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
        setStudents(out);
      } catch (e: any) {
        setErr(e?.message || "Failed to load students.");
      } finally {
        setLoadingStudents(false);
      }
    })();
  }, []);

  const selectedStudent = useMemo(
    () => students.find((s) => s.id === studentId) || null,
    [students, studentId]
  );

  useEffect(() => {
    (async () => {
      if (!selectedStudent?.studentKey) {
        setPeople([]);
        setPeopleMode(null);
        setSelectedTeacherId(null);
        setSelectedParentIds([]);
        setIncludeOtherParents(false);
        return;
      }

      setLoadingPeople(true);
      setErr(null);
      setSelectedTeacherId(null);
      setSelectedParentIds([]);
      setIncludeOtherParents(false);

      try {
        const fn = httpsCallable(functions, "getMessagingRosterForStudent");
        const res: any = await fn({ studentKey: selectedStudent.studentKey });
        const data = res?.data || {};

        if (data.mode === "teachers") {
          setPeopleMode("teachers");
          setPeople(Array.isArray(data.teachers) ? data.teachers : []);
        } else {
          setPeopleMode("parents");
          setPeople(Array.isArray(data.parents) ? data.parents : []);
        }
      } catch (e: any) {
        setErr(e?.message || "Failed to load messaging roster.");
        setPeople([]);
        setPeopleMode(null);
      } finally {
        setLoadingPeople(false);
      }
    })();
  }, [selectedStudent?.studentKey]);

  const filteredStudents = useMemo(() => {
    const f = normalize(studentFilter);
    if (!f) return students;
    return students.filter((s) =>
      `${s.fullName} ${s.studentKey} ${s.grade || ""} ${(s.specialAssignments || []).join(" ")}`
        .toLowerCase()
        .includes(f)
    );
  }, [students, studentFilter]);

  const filteredPeople = useMemo(() => {
    const f = normalize(peopleFilter);
    if (!f) return people;
    return people.filter((p) =>
      `${p.fullName || ""} ${p.email || ""} ${p.uid || ""} ${p.id || ""}`
        .toLowerCase()
        .includes(f)
    );
  }, [people, peopleFilter]);

  const selectedTeacher = useMemo(
    () =>
      filteredPeople.find((p) => personKey(p) === selectedTeacherId) ||
      people.find((p) => personKey(p) === selectedTeacherId) ||
      null,
    [filteredPeople, people, selectedTeacherId]
  );

  const selectedParents = useMemo(() => {
    const set = new Set(selectedParentIds);
    return people.filter((p) => set.has(personKey(p)));
  }, [people, selectedParentIds]);

  function toggleParentSelection(id: string) {
    setSelectedParentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  async function startThread() {
    if (!selectedStudent?.studentKey) {
      setErr("Select a student.");
      return;
    }

    setBusy(true);
    setErr(null);

    try {
      const fn = httpsCallable(functions, "createDirectThread");

      if (peopleMode === "parents") {
        const parentUids = Array.from(
          new Set(
            selectedParents
              .map((p) => asString(p.uid) || asString(p.id))
              .filter(Boolean)
          )
        );

        if (parentUids.length === 0) {
          setBusy(false);
          setErr("Select at least one parent.");
          return;
        }

        const res: any = await fn({
          studentKey: selectedStudent.studentKey,
          otherUids: parentUids,
        });

        const threadId = String(res?.data?.threadId || "");
        if (!threadId) throw new Error("createDirectThread did not return a threadId.");

        nav(`/messaging?threadId=${encodeURIComponent(threadId)}`);
        return;
      }

      const teacherUid = asString(selectedTeacher?.uid) || asString(selectedTeacher?.id);
      if (!teacherUid) {
        setBusy(false);
        setErr("Select a teacher/staff recipient.");
        return;
      }

      const res: any = await fn({
        studentKey: selectedStudent.studentKey,
        otherUid: teacherUid,
        includeOtherParents,
      });

      const threadId = String(res?.data?.threadId || "");
      if (!threadId) throw new Error("createDirectThread did not return a threadId.");

      nav(`/messaging?threadId=${encodeURIComponent(threadId)}`);
    } catch (e: any) {
      setErr(e?.message || "Failed to create/open direct thread.");
    } finally {
      setBusy(false);
    }
  }

  const canOpen =
    !!selectedStudent &&
    (
      (peopleMode === "parents" && selectedParentIds.length > 0) ||
      (peopleMode === "teachers" && !!selectedTeacherId)
    );

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <h2 style={{ marginTop: 0 }}>New Direct Message</h2>
          <p className="muted" style={{ marginTop: 6 }}>
            Pick a student you are authorized to message for, then pick the matching recipient.
          </p>
        </div>

        <div>
          <button className="btn secondary" onClick={() => nav("/messaging")}>
            Back to Messaging
          </button>
        </div>
      </div>

      {err ? <div className="error">{err}</div> : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginTop: 16,
        }}
      >
        <div className="card messaging-compose-panel">
          <h3 style={{ marginTop: 0 }}>1. Select Student</h3>

          <input
            className="input"
            placeholder="Search student name, key, grade, or specials…"
            value={studentFilter}
            onChange={(e) => setStudentFilter(e.target.value)}
          />

          <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
            {filteredStudents.length} / {students.length} students
          </div>

          <hr className="hr" />

          <div style={{ maxHeight: 520, overflow: "auto", display: "grid", gap: 8 }}>
            {loadingStudents ? (
              <div className="muted">Loading students…</div>
            ) : filteredStudents.length === 0 ? (
              <div className="muted">No students found.</div>
            ) : (
              filteredStudents.map((s) => {
                const selected = s.id === studentId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStudentId(s.id)}
                    className={`messaging-choice ${selected ? "selected" : ""}`}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{labelStudent(s)}</div>
                    <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                      studentKey: {s.studentKey}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="card messaging-compose-panel">
          <h3 style={{ marginTop: 0 }}>
            2. Select {peopleMode === "teachers" ? "Teacher / Staff" : "Parent / Guardian"}
          </h3>

          <input
            className="input"
            placeholder={peopleMode === "teachers" ? "Search teacher or staff…" : "Search parent or guardian…"}
            value={peopleFilter}
            onChange={(e) => setPeopleFilter(e.target.value)}
            disabled={!selectedStudent}
          />

          <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
            {!selectedStudent
              ? "Select a student first"
              : peopleMode === "parents"
              ? `${selectedParentIds.length} selected • ${filteredPeople.length} / ${people.length} recipients`
              : `${filteredPeople.length} / ${people.length} recipients`}
          </div>

          <hr className="hr" />

          {peopleMode === "parents" ? (
            <div className="muted" style={{ marginBottom: 10, fontSize: 12 }}>
              Select one or more linked parents/guardians for this student.
            </div>
          ) : peopleMode === "teachers" ? (
            <div className="muted" style={{ marginBottom: 10, fontSize: 12 }}>
              Select the teacher/staff recipient. You can also include other guardians on the parent side.
            </div>
          ) : null}

          <div style={{ maxHeight: 520, overflow: "auto", display: "grid", gap: 8 }}>
            {!selectedStudent ? (
              <div className="muted">Choose a student to load recipients.</div>
            ) : loadingPeople ? (
              <div className="muted">Loading recipients…</div>
            ) : filteredPeople.length === 0 ? (
              <div className="muted">No recipients found for that student.</div>
            ) : peopleMode === "parents" ? (
              filteredPeople.map((p) => {
                const pid = personKey(p);
                const selected = selectedParentIds.includes(pid);

                return (
                  <button
                    key={pid}
                    type="button"
                    onClick={() => toggleParentSelection(pid)}
                    className={`messaging-choice ${selected ? "selected" : ""}`}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontWeight: 800 }}>{labelPerson(p)}</div>
                      <div
                        className="messaging-choice-action"
                        style={{ minWidth: 20, textAlign: "right" }}
                      >
                        {selected ? "✓" : "○"}
                      </div>
                    </div>
                    <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                      {p.email || p.uid || p.id}
                    </div>
                  </button>
                );
              })
            ) : (
              filteredPeople.map((p) => {
                const pid = personKey(p);
                const selected = pid === selectedTeacherId;
                return (
                  <button
                    key={pid}
                    type="button"
                    onClick={() => setSelectedTeacherId(pid)}
                    className={`messaging-choice ${selected ? "selected" : ""}`}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{labelPerson(p)}</div>
                    <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                      {p.email || p.uid || p.id}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {peopleMode === "teachers" && selectedStudent ? (
            <div
              className="card messaging-preview-box"
              style={{
                padding: 12,
                marginTop: 12,
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  cursor: "pointer",
                }}
              >
                <div>
                  <div style={{ fontWeight: 800 }}>Include other guardians on this student</div>
                  <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                    Add the other linked parents/guardians for this student to the conversation too.
                  </div>
                </div>

                <input
                  type="checkbox"
                  checked={includeOtherParents}
                  onChange={(e) => setIncludeOtherParents(e.target.checked)}
                />
              </label>
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
        <button className="btn secondary" onClick={() => nav("/messaging")}>
          Cancel
        </button>
        <button className="btn" disabled={busy || !canOpen} onClick={startThread}>
          {busy ? "Opening…" : "Open Thread"}
        </button>
      </div>
    </div>
  );
}
